<template>
	<!-- slide-container: marca o conteúdo para o ModalContainer não fechar no clique interno !-->
	<!-- sm:items-stretch: o rastreio acompanha a altura do resumo, em vez de cortar antes !-->
	<div class="slide-container pointer-events-auto m-auto flex max-h-screen w-full max-w-5xl flex-col items-start gap-4 overflow-y-auto sm:flex-row sm:items-stretch">
		<!-- Resumo !-->
		<div class="w-full overflow-hidden rounded-lg bg-white shadow-xl sm:flex-1">
			<!-- Carregando !-->
			<template v-if="isLoading">
				<!-- relative: o Loading se posiciona por absolute !-->
				<div class="relative p-12">
					<Loading use-spinner :overlay="false"/>
				</div>
			</template>
			<!-- Erro !-->
			<template v-else-if="hasError">
				<div class="p-8 text-center">
					<p class="text-sm text-gray-700">Não foi possível carregar a remessa.</p>
					<button type="button"
							class="mt-4 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
							@click="load">
						Tentar de novo
					</button>
				</div>
			</template>
			<!-- Resumo !-->
			<template v-else-if="hasShipment">
				<!-- Cabeçalho !-->
				<header class="flex items-start gap-4 border-b border-gray-200 px-6 py-4">
					<div class="min-w-0 flex-1">
						<div class="flex items-center gap-3">
							<h2 class="truncate text-lg font-semibold text-gray-900">{{ data.tracking_code }}</h2>
							<ShipmentStatusBadge v-if="hasStatus" :shipment="data"/>
						</div>
						<p class="mt-1 truncate text-xs text-gray-500">{{ subtitle }}</p>
					</div>
					<!-- Fechar !-->
					<button type="button"
							class="-mr-2 -mt-1 flex-none rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-turquoise"
							aria-label="Fechar"
							@click="$emit('close')">
						<i class="fal fa-times"></i>
					</button>
				</header>
				<!-- Trajeto: é o que identifica a remessa, então abre o resumo !-->
				<section class="flex items-center gap-4 bg-gray-50 px-6 py-5">
					<!-- Origem !-->
					<div class="min-w-0 flex-1">
						<p class="text-xs font-medium uppercase text-gray-500">Origem</p>
						<p class="mt-1 truncate text-sm font-semibold text-gray-900">{{ data.from_name || '—' }}</p>
						<p class="truncate text-xs text-gray-500">{{ place(data, 'from') }}</p>
					</div>
					<!-- Trajeto !-->
					<div class="flex-none px-2 text-gray-300">
						<i class="fal fa-plane"></i>
					</div>
					<!-- Destino !-->
					<div class="min-w-0 flex-1 text-right">
						<p class="text-xs font-medium uppercase text-gray-500">Destino</p>
						<p class="mt-1 truncate text-sm font-semibold text-gray-900">{{ data.to_name || '—' }}</p>
						<p class="truncate text-xs text-gray-500">{{ place(data, 'to') }}</p>
					</div>
				</section>
				<!-- Números !-->
				<section class="grid grid-cols-2 gap-px border-y border-gray-200 bg-gray-200 sm:grid-cols-4">
					<div v-for="metric in metrics" :key="metric.label" class="bg-white px-4 py-3">
						<p class="text-xs text-gray-500">{{ metric.label }}</p>
						<p class="mt-1 truncate text-sm font-semibold text-gray-900">{{ metric.value }}</p>
					</div>
				</section>
				<!-- Detalhes !-->
				<section class="px-6 py-5">
					<dl class="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
						<div v-for="detail in details" :key="detail.label" class="min-w-0">
							<dt class="text-xs text-gray-500">{{ detail.label }}</dt>
							<dd class="mt-1 truncate text-sm text-gray-900">{{ detail.value }}</dd>
						</div>
					</dl>
				</section>
				<!-- Itens: a lista rola dentro da seção para o resumo não crescer sem fim !-->
				<section v-if="hasItems" class="border-t border-gray-200 px-6 py-5">
					<h3 class="text-xs font-medium uppercase text-gray-500">Itens</h3>
					<ul class="mt-3 max-h-48 divide-y divide-gray-100 overflow-y-auto">
						<li v-for="(item, index) in items" :key="item.id || index" class="flex items-baseline gap-3 py-2 first:pt-0">
							<span class="w-8 flex-none text-sm tabular-nums text-gray-500">{{ item.quantity || 1 }}x</span>
							<div class="min-w-0 flex-1">
								<p class="truncate text-sm text-gray-900">{{ item.description || item.sku || '—' }}</p>
								<p v-if="itemCodes(item)" class="truncate text-xs text-gray-500">{{ itemCodes(item) }}</p>
							</div>
							<span class="flex-none text-sm tabular-nums text-gray-900">{{ itemTotal(item) }}</span>
						</li>
					</ul>
				</section>
				<!-- Ações !-->
				<footer class="flex items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4">
					<router-link v-if="data.id"
								 :to="{ name: 'shipments.show', params: { id: data.id } }"
								 class="inline-flex items-center gap-x-1.5 rounded-md bg-turquoise px-3 py-2 text-sm font-semibold text-white shadow-sm hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-turquoise focus:ring-offset-2">
						<i class="fal fa-arrow-up-right-from-square"></i>
						Abrir remessa
					</router-link>
					<button type="button"
							class="ml-auto rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300"
							@click="$emit('close')">
						Fechar
					</button>
				</footer>
			</template>
		</div>
		<!-- Rastreio: card à parte, para o resumo não crescer com o histórico !-->
		<ShipmentTraceCard v-if="hasShipment && !isLoading && !hasError" :feed="data.feed"/>
	</div>
</template>

<script>
import Shipments from "@/API/Shipments.js"
import LoadingMixin from "@/mixins/loading.js"
import Loading from "@/components/Loading.vue"
import ShipmentStatusBadge from "@/domain/shipments/ShipmentStatusBadge.vue"
import ShipmentTraceCard from "@/domain/shipments/ShipmentTraceCard.vue"

export default {
	name: "ShipmentSummaryModal",
	mixins: [LoadingMixin],
	components: {Loading, ShipmentStatusBadge, ShipmentTraceCard},
	props: {
		/**
		 * Remessa já carregada. Quando ausente, buscamos pelo shipmentId
		 */
		shipment: Object,
		shipmentId: [Number, String]
	},
	data() {
		return {
			loaded_shipment: null,
			hasError: false
		}
	},
	computed: {
		/**
		 * Remessa exibida: a que veio por prop ou a que carregamos
		 * @returns {Object}
		 */
		data() {
			return this.loaded_shipment ?? this.shipment ?? {}
		},
		hasShipment() {
			return filled(this.data?.id) || filled(this.data?.tracking_code)
		},
		hasStatus() {
			return filled(this.data?.shipment_status)
		},
		/**
		 * Parceiro e data de criação
		 * @returns {string}
		 */
		subtitle() {

			const subtitle = []

			const partner = this.$data_get(this.data, 'partner.name')

			if (filled(partner)) subtitle.push(partner)

			if (filled(this.data.created_at)) subtitle.push(`criada em ${this.$utcDisplayDateTime(this.data.created_at)}`)

			return subtitle.join(' · ')
		},
		/**
		 * Itens da remessa
		 * @returns {Array}
		 */
		items() {
			return this.$data_get(this.data, 'items', []) || []
		},
		hasItems() {
			return this.items.length > 0
		},
		/**
		 * Números que resumem o volume da remessa
		 * @returns {Array}
		 */
		metrics() {
			return [
				{label: 'Itens', value: this.quantity},
				{label: 'Peso taxado', value: this.weight},
				{label: 'Cubagem', value: this.cubage},
				{label: 'Total', value: this.total}
			]
		},
		/**
		 * Quantidade somada dos itens, que é o que o cliente conta
		 * @returns {string}
		 */
		quantity() {

			if (!this.hasItems) return this.value(this.data.package_item_quantity)

			return String(this.items.reduce((total, item) => total + Number(item.quantity || 0), 0))
		},
		/**
		 * Peso taxado: é o que entra na conta do frete
		 * @returns {string}
		 */
		weight() {

			const weight = this.data.chargeable_weight_formated ?? this.data.chargeable_weight

			return filled(weight) ? `${weight} kg` : '—'
		},
		/**
		 * @returns {string}
		 */
		cubage() {

			const cubage = this.data.cubic_dimensions_formated ?? this.data.cubic_dimensions

			return filled(cubage) ? `${cubage} m³` : '—'
		},
		/**
		 * Total da remessa, já formatado pelo backend
		 * @returns {string}
		 */
		total() {
			return this.data.total_formated ?? this.value(this.data.total)
		},
		/**
		 * Dados de identificação, sem as linhas que a remessa não tem
		 * @returns {Array}
		 */
		details() {
			return [
				{label: 'Destinatário', value: this.value(this.data.to_name)},
				{label: 'Documento', value: this.value(this.data.to_document)},
				{label: 'E-mail', value: this.value(this.data.to_email)},
				{label: 'Telefone', value: this.value(this.$data_get(this.data, 'to_phone.value'))},
				{label: 'Código do cliente', value: this.value(this.data.tracking_code_label)},
				{label: 'Pedido', value: this.value(this.data.order_number)},
				{label: 'Frete', value: this.money(this.data.total_freight)},
				{label: 'Seguro', value: this.insurance},
				{label: 'MAWB', value: this.mawb}
			].filter(detail => detail.value !== '—')
		},
		/**
		 * @returns {string}
		 */
		insurance() {

			if (!this.data.with_insurance) return 'Sem seguro'

			return this.money(this.data.total_insurance)
		},
		/**
		 * @returns {string}
		 */
		mawb() {

			const mawbs = this.$data_get(this.data, 'mawb_ids', []) || []

			return mawbs.map(mawb => mawb.code).filter(filled).join(', ') || '—'
		}
	},
	methods: {
		/**
		 * Valor exibível, com traço no lugar de vazio
		 * @param value
		 * @returns {string}
		 */
		value(value) {
			return filled(value) ? String(value) : '—'
		},
		/**
		 * Valor monetário, com traço no lugar de vazio
		 * @param value
		 * @returns {string}
		 */
		money(value) {
			return filled(value) ? this.$formatCurrency(value) : '—'
		},
		/**
		 * SKU e NCM do item, quando informados
		 * @param item { Object }
		 * @returns {string}
		 */
		itemCodes(item) {
			return [item.sku, item.hscode].filter(filled).join(' · ')
		},
		/**
		 * Total do item, preferindo o valor já formatado pelo backend
		 * @param item { Object }
		 * @returns {string}
		 */
		itemTotal(item) {
			return filled(item.total_formated) ? item.total_formated : this.value(item.total)
		},
		/**
		 * Cidade e país de uma das pontas
		 * @param data { Object }
		 * @param prefix { String } from | to
		 * @returns {string}
		 */
		place(data, prefix) {

			const country = this.$data_get(data, `${prefix}_country`)

			return [
				data[`${prefix}_city`],
				country ? this.$localize(country, 'name_locale') : null
			].filter(filled).join(', ')
		},
		/**
		 * Action load
		 * @returns {Promise<boolean>}
		 */
		async load() {

			if (!filled(this.shipmentId)) return false

			console.debug('Shipments/SummaryModal@load: Loading data.', {id: this.shipmentId})

			this.hasError = false

			this.loading()
			const [err, res] = await Shipments.get(this.shipmentId)
			this.loaded()

			if (err) {
				console.error('Shipments/SummaryModal@load: Error on loading data.', {err, id: this.shipmentId})
				this.hasError = true
				return false
			}

			this.loaded_shipment = res.data?.data

			console.debug('Shipments/SummaryModal@load: Data loaded.')

			return true
		}
	},
	mounted() {
		// A remessa recebida por prop costuma vir da listagem, sem os contadores e
		// os totais do resumo, então buscamos os dados completos quando temos o id.
		if (filled(this.shipmentId)) this.load()
	}
}
</script>
